/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from "react";
import { Stack } from "@fluentui/react";
import { uniqueGUID } from "../../helpers/util";

export enum ValidationState {
  VALID = "VALID",
  INVALID = "INVALID",
  UNKNOWN = "UNKNOWN",
}

export type FieldsStates = Record<string, ValidationState | Record<string, ValidationState>>;
export type FormValues = Record<string, any>;
export type FormErrors = Record<string, string[] | undefined>;

export interface FormState {
  registerFieldLabel: (field: string, label?: string) => void;
  registerFieldGroup: (field: string, group?: string) => void;
  registerField: (field: string, group?: string) => void;
  unRegisterField: (field: string, group?: string) => void;
  getFieldLabel: (field: string) => string | undefined;
  getFieldLabelMap: () => Map<string, string>;
  getFieldGroup: (field: string) => string | undefined;
  getFieldGroupMap: () => Map<string, string>;
  setFieldState: (state: ValidationState, field: string, group?: string) => void;
  getFieldState: (field: string, group?: string) => ValidationState;
  getFormState: () => ValidationState;
  getGroupState: (group: string) => ValidationState;
  setValue: (value: any, field: string, group?: string) => void;
  getValue: (field: string, group?: string) => any;
  getValuesByFieldPrefix: (fieldPrefix: string, group?: string) => FormValues;
  getValues: () => FormValues;
  hasValidation: (group?: string) => boolean;
  getGroupValues: (group: string) => FormValues;
  deleteField: (field: string, group?: string) => void;
  getFieldErrors: (field: string, group?: string) => string[] | undefined;
}

export interface FormInterface {
  submit: () => void;
  enableValidation: (group?: string) => void;
  isValidationEnabled: (group?: string) => boolean;
  getFormState: () => ValidationState;
  hasValidation: (group?: string) => boolean;
  getFormValues: () => FormValues;
  getFormFieldLabelMap: () => Map<string, string>;
  getFormFieldGroupMap: () => Map<string, string>;
}

export interface FormStates {
  overall: ValidationState;
  groups: Record<string, ValidationState>;
}

export type StatesUpdateCallback = (
  formStates: FormStates, fieldState: ValidationState, field: string, group?: string,
) => void;

type ValueUpdateCallback = (formValues: FormValues, fieldValue: any, field: string, group?: string) => void;

class FormStateInstance implements FormState {
  private groups: Set<string> = new Set<string>();

  private fieldLabelMap: Map<string, string> = new Map<string, string>();

  private fieldGroupMap: Map<string, string> = new Map<string, string>();

  private onStatesUpdate?: StatesUpdateCallback;

  private onValueUpdate?: ValueUpdateCallback;

  private fieldsStates: FieldsStates = {};

  private formValues: FormValues = {};

  private formErrors: FormErrors = {};

  constructor(onStatesUpdate?: StatesUpdateCallback, onValueUpdate?: ValueUpdateCallback) {
    if (onStatesUpdate) {
      this.onStatesUpdate = onStatesUpdate;
    }
    if (onValueUpdate) {
      this.onValueUpdate = onValueUpdate;
    }
  }

  public registerFieldLabel = (field: string, label?: string): void => {
    if (label) {
      this.fieldLabelMap.set(field, label);
    }
  };

  public registerFieldGroup = (field: string, group?: string): void => {
    if (group) {
      this.fieldGroupMap.set(field, group);
    }
  };

  public registerField = (field: string, group?: string): void => {
    if (group) {
      if (!this.groups.has(group)) {
        this.groups.add(group);
        this.fieldsStates[group] = {};
      }
      const groupStates = (this.fieldsStates[group] as Record<string, ValidationState>);
      if (groupStates) {
        groupStates[field] = ValidationState.UNKNOWN;
      }
    } else {
      this.fieldsStates[field] = ValidationState.UNKNOWN;
    }
    this.onStatesUpdate?.(this.getStates(), ValidationState.UNKNOWN, field, group);
  };

  public unRegisterField = (field: string, group?: string): void => {
    if (group) {
      const groupStates = (this.fieldsStates[group] as Record<string, ValidationState>);
      if (groupStates) {
        delete groupStates[field];
      }
    } else {
      delete this.fieldsStates[field];
    }
    this.onStatesUpdate?.(this.getStates(), ValidationState.UNKNOWN, field, group);
  };

  public getFieldLabel = (field: string): string | undefined => this.fieldLabelMap.get(field);

  public getFieldLabelMap = (): Map<string, string> => this.fieldLabelMap;

  public getFieldGroup = (field: string): string | undefined => this.fieldGroupMap.get(field);

  public getFieldGroupMap = (): Map<string, string> => this.fieldGroupMap;

  public setFieldState = (state: ValidationState, field: string, group?: string): void => {
    if (group) {
      const groupStates = (this.fieldsStates[group] as Record<string, ValidationState>);
      if (groupStates) {
        groupStates[field] = state;
      }
    } else {
      this.fieldsStates[field] = state;
    }
    this.onStatesUpdate?.(this.getStates(), state, field, group);
  };

  private getStates = (): FormStates => {
    const states = {
      overall: this.getFormState(),
      groups: {},
    } as FormStates;
    this.groups.forEach(group => {
      states.groups[group] = this.getGroupState(group);
    });
    return states;
  };

  public getFieldState = (field: string, group?: string): ValidationState => {
    if (group) {
      return (this.fieldsStates[group] as Record<string, ValidationState>)[field] || ValidationState.UNKNOWN;
    }
    return this.fieldsStates[field] as ValidationState || ValidationState.UNKNOWN;
  };

  public getFormState = (): ValidationState => {
    const names = Object.keys(this.fieldsStates);
    const states = names.map(name => {
      if (!this.groups.has(name)) {
        return this.fieldsStates[name] as ValidationState;
      }
      const groupStates = this.fieldsStates[name] as Record<string, ValidationState>;
      return this.getConsolidatedState(Object.values(groupStates));
    });
    return this.getConsolidatedState(states);
  };

  public getGroupState = (group: string): ValidationState => {
    const groupStates = Object.values(this.fieldsStates[group] as Record<string, ValidationState>);
    return this.getConsolidatedState(groupStates);
  };

  private getConsolidatedState = (states: ValidationState[]): ValidationState => (
    states.some(state => state === ValidationState.INVALID)
      ? ValidationState.INVALID
      : states.every(state => state === ValidationState.VALID)
        ? ValidationState.VALID
        : ValidationState.UNKNOWN);

  public setValue = (value: any, field: string, group?: string): void => {
    if (group) {
      if (!this.formValues[group]) {
        this.formValues[group] = {};
      }
      (this.formValues[group] as Record<string, any>)[field] = value;
    } else {
      this.formValues[field] = value;
    }
    this.onValueUpdate?.(this.formValues, value, field, group);
  };

  public getValue = (field: string, group?: string): any => getValue(this.formValues, field, group);

  public getValuesByFieldPrefix = (
    fieldPrefix: string,
    group?: string,
  ): FormValues => getValuesByFieldPrefix(this.formValues, fieldPrefix, group);

  public getValues = (): FormValues => this.formValues;

  public hasValidation = (group?: string): boolean => {
    if (group) {
      return !!this.fieldsStates[group];
    }
    const names = Object.keys(this.fieldsStates);
    return names.length > 0;
  };

  public getGroupValues = (group: string): FormValues => this.formValues[group];

  public deleteField = (field: string, group?: string): void => {
    if (group) {
      delete this.formValues[group][field];
    } else {
      delete this.formValues[field];
    }
  };

  public getFieldErrors = (
    field: string,
    group?: string,
  ): string[] | undefined => getFieldErrors(this.formErrors, field, group);

  public setFieldErrors = (errors: string[] | undefined, field: string, group?: string): void => {
    this.formErrors[String([field, group])] = errors;
    this.setFieldState(errors ? ValidationState.INVALID : ValidationState.VALID, field, group);
  };

  public getFormErrors = (): FormErrors => this.formErrors;
}

export const getValue = (formValues: FormValues, field: string, group?: string): any => {
  if (group) {
    return (formValues[group] as Record<string, any>)?.[field];
  }
  return formValues[field];
};

export const getValuesByFieldPrefix = (
  formValues: FormValues,
  fieldPrefix: string,
  group?: string,
): FormValues => {
  const result: FormValues = {};
  let formValuesSubset: FormValues;
  if (group) {
    formValuesSubset = formValues[group];
  } else {
    formValuesSubset = formValues;
  }
  if (formValuesSubset) {
    Object.keys(formValuesSubset).forEach(key => {
      if (key.startsWith(fieldPrefix)) {
        result[key] = formValuesSubset[key];
      }
    });
  }
  return result;
};

export const getFieldErrors = (
  formErrors: FormErrors,
  field: string,
  group?: string,
): string[] | undefined => formErrors[String([field, group])];

export const FormContext = React.createContext<FormState>({} as FormState);
export const FormContextProvider = FormContext.Provider;

export enum FormValidationMode {
  ON = "ON",
  OFF = "OFF",
}

export type FormGroupsValidationModes = Record<string, FormValidationMode>;

export const FormValidationModeContext = React.createContext<FormValidationMode>(FormValidationMode.OFF);
export const FormValidationModeContextProvider = FormValidationModeContext.Provider;

export const FormGroupValidationModeContext = React.createContext<FormGroupsValidationModes>(
  {} as FormGroupsValidationModes,
);
export const FormGroupValidationModeContextProvider = FormGroupValidationModeContext.Provider;

export const FormErrorsContext = React.createContext<FormErrors>({});
export const FormErrorsContextProvider = FormErrorsContext.Provider;

interface ConsolidatedValidationModes {
  overall: FormValidationMode;
  groups: FormGroupsValidationModes;
}

export interface FormFieldErrors {
  errors: string[] | undefined;
  field: string;
  group?: string;
}
export interface FormProps {
  componentRef?: (form: FormInterface) => void;
  children: React.ReactNode;
  groups?: string[];
  onStatesUpdate?: StatesUpdateCallback;
  onValueUpdate?: ValueUpdateCallback;
  onSubmit?: (formValues: FormValues) => void;
  validator?: (formValues: FormValues) => FormFieldErrors[] | undefined;
}

export const Form = (
  { componentRef, children, groups: registeredGroups, onStatesUpdate, onValueUpdate, onSubmit, validator }: FormProps,
): JSX.Element => {
  const internalOnStatesUpdate = (
    formStates: FormStates,
    fieldState: ValidationState,
    field: string,
    group?: string,
  ): void => {
    setOverallFormState(formStates.overall);
    if (onStatesUpdate) {
      onStatesUpdate(formStates, fieldState, field, group);
    }
  };

  const internalOnValueUpdate = (formValues: FormValues, fieldValue: any, field: string, group?: string): void => {
    setFormValuesChanged(uniqueGUID());
    if (onValueUpdate) {
      onValueUpdate(formValues, fieldValue, field, group);
    }
  };

  const processValidatorResult = (formErrors: FormFieldErrors[] | undefined): FormErrors => {
    formErrors?.forEach(formError => {
      formStateInstanceRef.current.setFieldErrors(formError.errors, formError.field, formError.group);
    });
    return formStateInstanceRef.current.getFormErrors();
  };

  const formStateInstanceRef = React.useRef<FormStateInstance>(
    new FormStateInstance(internalOnStatesUpdate, internalOnValueUpdate),
  );
  const [validationMode, setValidationMode] = React.useState<FormValidationMode>(FormValidationMode.OFF);
  const [groupsValidationModes, setGroupsValidationModes] = React.useState<FormGroupsValidationModes>(() => {
    const validationModes = {} as FormGroupsValidationModes;
    registeredGroups?.forEach(registeredGroup => { validationModes[registeredGroup] = FormValidationMode.OFF; });
    return validationModes;
  });

  // Sync the validation mode between the states and this ref.
  // The ref is needed for access by the component ref methods to avoid the JS closure accessing stalled state values
  const validationModesRef = React.useRef<ConsolidatedValidationModes>({} as ConsolidatedValidationModes);

  const [overallFormState, setOverallFormState] = React.useState<ValidationState>(ValidationState.UNKNOWN);
  const [formValuesChanged, setFormValuesChanged] = React.useState<string>();
  const [serializedFormErrors, setSerializedFormErrors] = React.useState<string>(JSON.stringify({}));

  const submit = (): void => {
    if (onSubmit) {
      const values = formStateInstanceRef.current.getValues();
      onSubmit(values);
    }
  };

  const enableValidation = (group?: string): void => {
    if (group) {
      setGroupsValidationModes(prevGroupsValidationModes => {
        const validationModes = { ...prevGroupsValidationModes };
        validationModes[group] = FormValidationMode.ON;
        validationModesRef.current.groups = validationModes;
        return validationModes;
      });
    } else {
      setValidationMode(FormValidationMode.ON);
      validationModesRef.current.overall = FormValidationMode.ON;
    }
  };

  const isValidationEnabled = (group?: string): boolean => (
    group
      ? validationModesRef.current.groups?.[group] === FormValidationMode.ON
      : validationModesRef.current.overall === FormValidationMode.ON
  );

  const formRef = React.useRef<FormInterface>({
    submit,
    enableValidation,
    isValidationEnabled,
    getFormState: () => formStateInstanceRef.current.getFormState(),
    hasValidation: (group?: string) => formStateInstanceRef.current.hasValidation(group),
    getFormValues: () => formStateInstanceRef.current.getValues(),
    getFormFieldLabelMap: () => formStateInstanceRef.current.getFieldLabelMap(),
    getFormFieldGroupMap: () => formStateInstanceRef.current.getFieldGroupMap(),
  });

  React.useEffect(() => {
    if (componentRef) {
      componentRef(formRef.current);
    }
  }, [componentRef]);

  React.useEffect(() => {
    if (validationMode === FormValidationMode.ON && validator && overallFormState !== ValidationState.UNKNOWN) {
      const values = formStateInstanceRef.current.getValues();
      const formErrors = processValidatorResult(validator(values));
      setSerializedFormErrors(JSON.stringify(formErrors));
    }
  // Only if validation mode and overall form state/values change.
  // Others props should not have side effect beyond their initial values
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationMode, overallFormState, formValuesChanged]);

  return (
    <FormContextProvider value={formStateInstanceRef.current}>
      <FormValidationModeContextProvider value={validationMode}>
        <FormGroupValidationModeContextProvider value={groupsValidationModes}>
          <FormErrorsContextProvider value={JSON.parse(serializedFormErrors)}>
            {children}
          </FormErrorsContextProvider>
        </FormGroupValidationModeContextProvider>
      </FormValidationModeContextProvider>
    </FormContextProvider>
  );
};

export enum FormInputGroupLayout {
  WIDE = "WIDE",
  COMPACT = "COMPACT",
}

export interface FormInputGroupProps {
  groupName: string;
  layout?: FormInputGroupLayout;
  children: React.ReactNode;
  style?: React.CSSProperties;
}

export const FormInputGroupLayoutContext = React.createContext<{
  groupName: string,
  layout:FormInputGroupLayout
}>({ groupName: "", layout: FormInputGroupLayout.WIDE });
export const FormInputGroupLayoutContextProvider = FormInputGroupLayoutContext.Provider;

export const FormInputGroup = (
  { groupName, children, layout = FormInputGroupLayout.WIDE, style }: FormInputGroupProps,
): JSX.Element => (
  <FormInputGroupLayoutContextProvider value={{ groupName, layout }}>
    <Stack style={style}>
      {children}
    </Stack>
  </FormInputGroupLayoutContextProvider>
);
